/**
 * The Liability block of the Create NCR form: the liability choice, "handle
 * internally" for customer liability, and the required-actions Yes/No boxes.
 * Split out of NCRDetailsForm.tsx to keep it under the 500-line limit
 * (QUALITY-NCR-REVIEW round 3). Purely presentational.
 */
import { Label } from '../../ui/label';
import type { UserProfile } from '../../../lib/api';
import YesNoSelect from './YesNoSelect';
import type { NCRFormData } from './ncrFormTypes';

interface NCRLiabilitySectionProps {
  formData: NCRFormData;
  fieldErrors: Record<string, string>;
  updateField: (field: keyof NCRFormData, value: string | number | boolean | null) => void;
  /** Customer users an internal NCR may be assigned to. */
  customerUsers: UserProfile[];
}

export default function NCRLiabilitySection({
  formData, fieldErrors, updateField, customerUsers,
}: NCRLiabilitySectionProps) {
  return (
    <div className="border-t pt-4 mt-2">
      <h4 id="liability-heading" className="text-sm font-semibold mb-1">Liability <span className="text-red-500">*</span></h4>
      <p className="text-xs text-muted-foreground mb-3">Please Select</p>
      <div className="grid grid-cols-2 gap-3 max-w-lg" role="group" aria-labelledby="liability-heading" aria-invalid={!!fieldErrors['liability_type'] || undefined} aria-describedby={fieldErrors['liability_type'] ? 'liability_type-error' : undefined}>
        <button
          type="button"
          onClick={() => updateField('liability_type', 2)}
          className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
            formData.liability_type === 2
              ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
              : `${fieldErrors['liability_type'] ? 'border-red-500' : 'border-gray-200'} text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400`
          }`}
        >
          Customer Liability
        </button>
        <button
          type="button"
          onClick={() => updateField('liability_type', 1)}
          className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
            formData.liability_type === 1
              ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
              : `${fieldErrors['liability_type'] ? 'border-red-500' : 'border-gray-200'} text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400`
          }`}
        >
          Supplier Liability
        </button>
      </div>
      {fieldErrors['liability_type'] && <p id="liability_type-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['liability_type']}</p>}

      {/* Internal handling — Customer Liability only. Resolved in-house,
          never sent to the supplier; assigned to an internal user. */}
      {formData.liability_type === 2 && (
        <div className="mt-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-3 dark:border-amber-700 dark:bg-amber-900/10">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_internal}
              onChange={(e) => updateField('is_internal', e.target.checked)}
            />
            Handle internally (do not send to supplier)
          </label>
          {formData.is_internal && (
            <div className="mt-3">
              <Label className="text-xs">Assigned To (internal team)</Label>
              <select
                value={String(formData.assigned_to ?? '')}
                onChange={(e) => updateField('assigned_to', e.target.value ? Number(e.target.value) : null)}
                className={`w-full h-9 px-3 rounded-md border bg-background text-sm ${fieldErrors['assigned_to'] ? 'border-red-500' : 'border-input'}`}
                aria-invalid={!!fieldErrors['assigned_to'] || undefined}
                aria-describedby={fieldErrors['assigned_to'] ? 'assigned_to-error' : undefined}
              >
                <option value="">Unassigned</option>
                {customerUsers.map((u) => (
                  <option key={u.id} value={u.user.id}>
                    {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                  </option>
                ))}
              </select>
              {fieldErrors['assigned_to'] && <p id="assigned_to-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['assigned_to']}</p>}
            </div>
          )}
        </div>
      )}

      {formData.liability_type > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-muted-foreground mb-3">
            Required actions for the {formData.liability_type === 1 ? 'supplier' : 'customer'}:
          </p>
          <div className={`grid ${formData.liability_type === 1 ? 'grid-cols-2 sm:grid-cols-4 max-w-3xl' : 'grid-cols-2 max-w-lg'} gap-3`}>
            <YesNoSelect id="is_8d_required" label="8D Required" value={formData.is_8d_required} onChange={(v) => updateField('is_8d_required', v)} error={fieldErrors['is_8d_required']} />
            {!formData.is_internal && (
              <YesNoSelect id="is_collection_required" label="Collection Required" value={formData.is_collection_required} onChange={(v) => updateField('is_collection_required', v)} error={fieldErrors['is_collection_required']} />
            )}
            {formData.liability_type === 1 && (
              <YesNoSelect id="is_credit_required" label="Credit Required" value={formData.is_credit_required} onChange={(v) => updateField('is_credit_required', v)} error={fieldErrors['is_credit_required']} />
            )}
            {formData.liability_type === 1 && (
              <YesNoSelect id="is_replacement_required" label="Replacement Required" value={formData.is_replacement_required} onChange={(v) => updateField('is_replacement_required', v)} error={fieldErrors['is_replacement_required']} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
